import React from "react";
import { ArchitectureGlyph } from "./ArchitectureFlow";
import {
  governedExecutionSteps,
  phaseControlExamples,
  representativeWorkflowPhases,
  reworkLoops,
  roleResponsibilityRows,
  workflowFamilies,
} from "../../content/simplidevWorkflow";

function SectionLabel({ children }) {
  return <span className="direction-b-mono-label">{children}</span>;
}

function PhaseFlow() {
  return (
    <div className="direction-b-workflow-phase-flow" aria-label="Representative governed SimpliDev SDLC flow">
      <div className="direction-b-workflow-phase-flow__nodes">
        {representativeWorkflowPhases.map((phase) => (
          <div className="direction-b-workflow-phase-flow__node" key={phase.label}>
            <ArchitectureGlyph kind={phase.kind} />
            <strong>{phase.label}</strong>
            <span>{phase.note}</span>
          </div>
        ))}
      </div>
      <div className="direction-b-workflow-phase-flow__policy">
        <span>Phase policy / transition gates</span>
        <i aria-hidden="true" />
        <span>Agent discretion inside the phase; platform control over legal transitions and permitted operations.</span>
      </div>
      <p className="direction-b-workflow-caption">Representative standard-task path. Design is conditional for UI work; other workflow families use different phase sets and return paths.</p>
    </div>
  );
}

function PhaseControls() {
  return (
    <article className="direction-b-workflow-card direction-b-workflow-card--wide">
      <SectionLabel>Phase-aware execution controls</SectionLabel>
      <h3>Phases change what an agent may do.</h3>
      <p className="direction-b-workflow-card__intro">The workflow is not a sequence of labels. At the MCP/tool boundary, phase policy restricts sensitive operations based on the current workflow state.</p>
      <div className="direction-b-phase-control-grid">
        {phaseControlExamples.map((example) => (
          <div className="direction-b-phase-control" key={example.phase}>
            <div className="direction-b-phase-control__heading"><ArchitectureGlyph kind={example.kind} /><span>{example.phase}</span></div>
            <strong>{example.title}</strong>
            <p>{example.detail}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function RoleOrchestration() {
  return (
    <article className="direction-b-workflow-card direction-b-workflow-card--wide">
      <SectionLabel>Role-scoped agent orchestration</SectionLabel>
      <h3>Shared context does not mean shared authority.</h3>
      <p className="direction-b-workflow-card__intro">The collaborative workflow assigns responsibility by phase. Agents share the plan and durable state, but only the responsible role drives the work and advances the transition.</p>
      <div className="direction-b-role-matrix" role="table" aria-label="Role responsibility changes by phase">
        <div className="direction-b-role-matrix__row direction-b-role-matrix__row--header" role="row">
          <span role="columnheader">Phase</span><span role="columnheader">Business Analyst</span><span role="columnheader">Architect</span><span role="columnheader">Developer</span><span role="columnheader">Handoff</span>
        </div>
        {roleResponsibilityRows.map((row) => (
          <div className="direction-b-role-matrix__row" role="row" key={row.phase}>
            <strong role="rowheader" data-label="Phase">{row.phase}</strong><span role="cell" data-label="Business Analyst">{row.businessAnalyst}</span><span role="cell" data-label="Architect">{row.architect}</span><span role="cell" data-label="Developer">{row.developer}</span><span className="direction-b-role-matrix__handoff" role="cell" data-label="Handoff">{row.handoff}</span>
          </div>
        ))}
      </div>
      <div className="direction-b-rci-key"><span><b>R</b> Responsible · drives and advances</span><span><b>C</b> Consulted · answers and advises</span><span><b>I</b> Informed · observes the work</span></div>
    </article>
  );
}

function ReworkAndExecution() {
  return (
    <div className="direction-b-workflow-split">
      <article className="direction-b-workflow-card">
        <SectionLabel>Return paths</SectionLabel>
        <h3>Failure becomes a bounded rework path.</h3>
        <div className="direction-b-rework-list">
          {reworkLoops.map((loop) => (
            <div className="direction-b-rework-item" key={loop.label}>
              <strong>{loop.label}</strong>
              <div className="direction-b-rework-item__flow">{loop.flow.map((step, index) => <React.Fragment key={`${loop.label}-${step}-${index}`}><span className={index % 2 === 1 ? "direction-b-rework-item__return" : ""}>{step}</span>{index < loop.flow.length - 1 && <i aria-hidden="true">→</i>}</React.Fragment>)}</div>
              <p>{loop.detail}</p>
            </div>
          ))}
        </div>
      </article>
      <article className="direction-b-workflow-card">
        <SectionLabel>Governed execution loop</SectionLabel>
        <h3>State carries the work forward.</h3>
        <div className="direction-b-execution-steps">
          {governedExecutionSteps.map((step, index) => <div className="direction-b-execution-step" key={step.label}><span>{String(index + 1).padStart(2, "0")}</span><strong>{step.label}</strong><small>{step.method}</small></div>)}
        </div>
      </article>
    </div>
  );
}

export default function SimpliDevWorkflowMechanics() {
  return (
    <section className="direction-b-detail__section direction-b-workflow-mechanics" aria-labelledby="simplidev-workflow-mechanics-title">
      <div className="direction-b-workflow-mechanics__intro">
        <SectionLabel>Governed SDLC execution</SectionLabel>
        <h2 id="simplidev-workflow-mechanics-title">A governed SDLC beneath the broader product lifecycle.</h2>
        <p>Product and stakeholder needs belong to the broader PDLC. SimpliDev governs the autonomous SDLC beneath that context: phases, permitted actions, durable state, role authority, evaluation, review, and human control.</p>
      </div>
      <article className="direction-b-workflow-card direction-b-workflow-card--wide direction-b-workflow-card--flow">
        <SectionLabel>Representative governed SDLC flow</SectionLabel>
        <h3>Different workflow policies, one controlled execution model.</h3>
        <PhaseFlow />
      </article>
      <PhaseControls />
      <RoleOrchestration />
      <ReworkAndExecution />
      <article className="direction-b-workflow-card direction-b-workflow-card--wide direction-b-workflow-families">
        <SectionLabel>Workflow families</SectionLabel>
        <h3>Policy changes with the type of engineering work.</h3>
        <p>These diagrams are representative, not one universal sequence. SimpliDev applies different governed workflows to different work types.</p>
        <div className="direction-b-workflow-family-list">{workflowFamilies.map((family) => <span key={family}>{family}</span>)}</div>
      </article>
    </section>
  );
}
